import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Drawer,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import { ThunderboltOutlined } from '@ant-design/icons';
import CopyButton from '@/components/common/CopyButton';
import PayeeDetailDrawer from '@/components/payees/PayeeDetailDrawer';
import AiQuickFillModal from '@/components/payees/AiQuickFillModal';
import { originSummary } from '@/domain/payeeDetail';
import {
  PAYOUT_BUCKET_COLOR,
  PAYOUT_BUCKET_HINT,
  PAYOUT_BUCKET_LABEL,
  groupDigits,
} from '@/domain/payoutSummary';
import dayjs from 'dayjs';
import { apiErrorCode, apiErrorMessage, apiErrorFields } from '@/api/client';
import {
  getPayeeSchema,
  validatePayee,
  createPayee,
  listPayees,
  type AiExtractResult,
  type Payee,
  type PayeeFacets,
  type PayeeFilter,
} from '@/api/payees';
import {
  collectSubmitValues,
  conditionFrom,
  canonicalPath,
  mergeValues,
  preferSwift,
  shouldRefreshSchema,
  validateLocally,
  type FormSchema,
  type FormValues,
} from '@/domain/schemaForm';
import SchemaForm, { toErrorMap } from '@/components/payees/SchemaForm';
import FilterBar from '@/components/common/FilterBar';
import { useListFilter } from '@/hooks/useListFilter';
import { isAdminOrFinance } from '@/domain/roles';
import { useSessionStore } from '@/store/session';

const { Paragraph, Text } = Typography;

const EMPTY_FACETS: PayeeFacets = {
  bankCountryCodes: [],
  accountCurrencies: [],
  transferMethods: [],
};

const ENTITY_TYPES = [
  { value: 'PERSONAL', label: '个人' },
  { value: 'COMPANY', label: '企业' },
];

/** 见 PRODUCT.md §4.2：添加时只落本地草稿，第一次打款前才推给 Airwallex。 */
const SYNC_STATUSES = [
  { value: 'DRAFT', label: '草稿（未推送）' },
  { value: 'SYNCED', label: '已同步' },
];

const toOptions = (values: string[]) => values.map((v) => ({ value: v, label: v }));

/**
 * 收款人列表 + 创建。
 *
 * 创建流程刻意**不做分步向导**：字段之间是互相牵动的（改币种会换掉整组银行字段），
 * 分步会让用户在第 3 步改了一个选项、前两步的输入却已经不适用。一屏之内随选随刷新，
 * 反而更符合这份 schema 的实际行为。
 */
export default function Payees() {
  const { message } = App.useApp();
  const me = useSessionStore((s) => s.me);
  // member 能看这一页，但只能看：写操作是 admin / finance 的活（服务端同一条判定）。
  const canManage = isAdminOrFinance(me?.roles ?? []);

  const [rows, setRows] = useState<Payee[]>([]);
  const [facets, setFacets] = useState<PayeeFacets>(EMPTY_FACETS);
  const [loading, setLoading] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const f = useListFilter<PayeeFilter>();

  const [open, setOpen] = useState(false);
  /** 点开某一行看详情。null = 抽屉关着。 */
  const [detail, setDetail] = useState<Payee | null>(null);
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  /** 用户是否手动选过汇款方式——选过之后 preferSwift 就不能再用 SWIFT 覆盖他。 */
  const transferMethodTouched = useRef(false);
  /** 「AI 快速录入」弹窗。 */
  const [aiOpen, setAiOpen] = useState(false);
  const [sourceRecordId, setSourceRecordId] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items, facets: next } = await listPayees(f.filter);
      setRows(items);
      // facets 由服务端按「本组织全量」算，不跟着筛选收窄，所以每次都可以原样覆盖
      setFacets(next);
      setNotConfigured(false);
    } catch (err) {
      // 没配凭据是一个**可执行**的状态，不该只弹一句红字就算了
      if (apiErrorCode(err) === 'AIRWALLEX_NOT_CONFIGURED') setNotConfigured(true);
      else message.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message, f.filter]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  /**
   * 拉一版 schema，并把已填的值合并进来。
   * `keep` 为 false 时（首次打开）用 schema 自带的默认值起步。
   *
   * 🔴 **要循环拉，不能只拉一次。** 新 schema 的 `default` 可能补全出新的条件字段：
   * 选完国家 + 币种之后，「汇款方式」才会带着 `default: "LOCAL"` 出现，这时的条件比我们
   * 刚发出去的那一版更完整，必须拿新条件再拉一轮（4 个字段 → 18 个字段）。少这一轮，
   * 界面上就是「汇款方式已经显示 LOCAL，下面却什么都没长出来」，用户完全不知道该做什么。
   * 上限 3 轮纯粹是防御：条件字段总共就那么几个，正常最多两轮收敛。
   */
  const refreshSchema = useCallback(
    async (current: FormValues, keep: boolean) => {
      setSchemaLoading(true);
      try {
        let acc: FormValues = keep ? current : {};
        let latest: FormSchema | null = null;
        for (let round = 0; round < 3; round++) {
          /*
           * 🔴 第一轮排除**依赖条件**（当前只有 local_clearing_system）。
           *
           * 它的合法取值由其它条件决定：US/USD/LOCAL 下是 ACH/FEDWIRE/FEDNOW，
           * 切到 SWIFT 或换成德国之后这个字段在 schema 里压根不存在。用户改汇款方式时
           * 表单里还留着上一版的 'ACH'，带着它去问 Airwallex 会回
           * SCHEMA_DEFINITION_NOT_FOUND —— 第 0 轮就抛错，循环根本没机会收敛，
           * 界面上就是「切到 SWIFT，整个表单报错卡死」。
           *
           * 排除它不会丢用户的选择：新 schema 里若仍有这个字段，下面的 mergeValues
           * 会把旧值带回来，第二轮再作为条件发出去；若已经没有这个字段，值自然被丢掉，
           * 这正是我们要的。代价是用户显式选过清算系统时多一轮请求。
           */
          const sent = conditionFrom(acc, { skipDependent: round === 0 });
          const next = await getPayeeSchema(sent);
          latest = next;
          acc = mergeValues(next, acc);
          // 🔴 预选 SWIFT 要放在这里、收敛判断之前：预选出来的值要作为新条件参与
          // 下一轮请求，才能把 SWIFT 对应的字段（比如 swift_code）拉出来。
          acc = preferSwift(next, acc, transferMethodTouched.current);
          // 条件没再变化就收敛了
          if (JSON.stringify(sent) === JSON.stringify(conditionFrom(acc))) break;
        }
        if (latest) {
          setSchema(latest);
          setValues(acc);
          setErrors({});
        }
      } catch (err) {
        if (apiErrorCode(err) === 'AIRWALLEX_NOT_CONFIGURED') {
          setNotConfigured(true);
          setOpen(false);
        }
        message.error(apiErrorMessage(err));
      } finally {
        setSchemaLoading(false);
      }
    },
    [message],
  );

  async function startCreate() {
    setOpen(true);
    setSchema(null);
    setErrors({});
    transferMethodTouched.current = false;
    // 🔴 预置主体类型，用它去拉第一版 schema——绝大多数收款人是个人，
    // 让用户少点一次，且第一版 schema 就能长出更多字段。
    // keep 传 true：acc 从这里传入的 initial 起步（不是读 React 的 values 状态），
    // 所以既不会丢掉预置的 PERSONAL，也不会带出上一次打开表单时的残留值。
    const initial = { 'beneficiary.entity_type': 'PERSONAL' };
    setValues(initial);
    setSourceRecordId(undefined);
    await refreshSchema(initial, true);
  }

  /**
   * 字段变更。**改动了条件**的字段一动就要重新拉 schema —— 这不是优化，是必需：
   * 美国选了 LOCAL/ACH 之后才会长出整组银行字段，不重拉就永远填不全，
   * 提交时被 Airwallex 拒且用户看不出少了什么。
   *
   * 🔴 判据是 shouldRefreshSchema，不是字段上的 `refresh` 标记 —— 后者也挂在邮编这类
   * 自由输入框上，照它执行的话邮编根本打不进去（见 domain/schemaForm.ts 的说明）。
   */
  function onFieldChange(path: string, value: string) {
    // 用户手动动过汇款方式（哪怕选的还是 SWIFT）之后，preferSwift 就不能再帮他做主。
    if (canonicalPath(path) === 'transfer_method') transferMethodTouched.current = true;
    const next = { ...values, [path]: value };
    setValues(next);
    setErrors((e) => {
      const { [path]: _drop, ...rest } = e;
      return rest;
    });
    if (shouldRefreshSchema(values, next)) void refreshSchema(next, true);
  }

  /**
   * 把 AI 识别的结果填进表单（PRODUCT.md §4.9）。
   *
   * 值整体替换而不是合并：用户点这个按钮的意图就是「用这段内容重填」，
   * 把上一次残留的值和新识别的值混在一起，最后提交的会是一个谁都没见过的组合。
   * 填完按新条件重新拉 schema（keep=true，与手动改国家 / 币种走同一条路）。
   *
   * 🔴 AI 选了汇款方式就等于用户选了：把 transferMethodTouched 置 true，
   * 否则下一轮 refreshSchema 的 preferSwift 会把 AI 判出的 LOCAL 悄悄改回 SWIFT。
   */
  async function applyAi(result: AiExtractResult) {
    setAiOpen(false);
    if (Object.keys(result.values).some((p) => canonicalPath(p) === 'transfer_method')) {
      transferMethodTouched.current = true;
    }
    setValues(result.values);
    setSourceRecordId(result.sourceRecordId);
    setErrors({});
    await refreshSchema(result.values, true);
    const tail = result.missing.length > 0 ? `，还有 ${result.missing.length} 个必填字段待补` : '';
    message.success(`已填入 ${result.filled.length} 个字段${tail}`);
  }

  async function submit() {
    if (!schema) return;

    const local = validateLocally(schema, values);
    if (local.length > 0) {
      setErrors(toErrorMap(local));
      message.warning('请先补全标红的字段');
      return;
    }

    const payload = collectSubmitValues(schema, values);
    setSubmitting(true);
    try {
      // 先 validate 再 create：Airwallex 的 validate 是干跑，一次性返回所有不合规字段，
      // 直接 create 的话用户要一个错误一个错误地试。
      await validatePayee(payload);
      const created = await createPayee(payload, sourceRecordId);
      message.success(`已创建收款人 ${created.accountName ?? ''}`);
      setOpen(false);
      void load();
    } catch (err) {
      // 服务端把 Airwallex 的出错字段 path 透传了过来，标到具体那一格上
      const fields = apiErrorFields(err);
      if (fields.length > 0) {
        setErrors(
          toErrorMap(fields.map((path) => ({ path, message: '该字段不符合 Airwallex 要求' }))),
        );
      }
      message.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (notConfigured) {
    return (
      <Card title="收款人">
        <Empty
          description={
            <Space direction="vertical">
              <Text>本组织还没有配置 Airwallex 凭据</Text>
              <Text type="secondary">配置之后才能创建收款人。</Text>
            </Space>
          }
        >
          {me?.roles.includes('admin') ? (
            <Link to="/settings">
              <Button type="primary">去设置</Button>
            </Link>
          ) : (
            <Text type="secondary">请联系组织管理员在「设置」页配置。</Text>
          )}
        </Empty>
      </Card>
    );
  }

  return (
    <Card
      title="收款人"
      extra={
        canManage ? (
          <Button type="primary" onClick={startCreate}>
            新建收款人
          </Button>
        ) : null
      }
    >
      <FilterBar active={f.active} resultCount={rows.length} onReset={f.reset}>
        <Input
          allowClear
          style={{ width: 200 }}
          placeholder="搜名称 / 别名 / 短 id"
          value={f.keyword}
          onChange={(e) => f.setKeyword(e.target.value)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 130 }}
          placeholder="主体类型"
          options={ENTITY_TYPES}
          value={f.filter.entityType ?? []}
          onChange={(v: string[]) => f.set('entityType', v)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 130 }}
          placeholder="收款地区"
          options={toOptions(facets.bankCountryCodes)}
          value={f.filter.bankCountryCode ?? []}
          onChange={(v: string[]) => f.set('bankCountryCode', v)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 120 }}
          placeholder="币种"
          options={toOptions(facets.accountCurrencies)}
          value={f.filter.accountCurrency ?? []}
          onChange={(v: string[]) => f.set('accountCurrency', v)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 130 }}
          placeholder="汇款方式"
          options={toOptions(facets.transferMethods)}
          value={f.filter.transferMethod ?? []}
          onChange={(v: string[]) => f.set('transferMethod', v)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 150 }}
          placeholder="同步状态"
          options={SYNC_STATUSES}
          value={f.filter.syncStatus ?? []}
          onChange={(v: Array<'DRAFT' | 'SYNCED'>) => f.set('syncStatus', v)}
        />
      </FilterBar>

      <Table<Payee>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        // 与另外三个列表页一致：列宽加起来超过内容区时横向滚动，
        // 而不是把「名称」「来源」压成一字一行
        scroll={{ x: 'max-content' }}
        // 整行可点：这一页最常做的事是「看一眼这个人的收款信息」，
        // 而不是在十来个窄列里横向找字段
        onRow={(row) => ({ onClick: () => setDetail(row), style: { cursor: 'pointer' } })}
        columns={[
          {
            title: '短 id',
            dataIndex: 'shortId',
            width: 160,
            // 发给别人时报的就是这串码，所以直接给个复制按钮，不用进详情再复制
            render: (shortId: string) => (
              <Space size={2}>
                {/* 不许断行：短 id 被折成两行就没法一眼抄下来了 */}
                <Text code style={{ whiteSpace: 'nowrap' }}>
                  {shortId}
                </Text>
                <CopyButton text={shortId} tooltip="复制短 id" successMessage="短 id 已复制" />
              </Space>
            ),
          },
          {
            title: '名称',
            dataIndex: 'accountName',
            width: 220,
            render: (name: string | null, row) => (
              <Space direction="vertical" size={0}>
                <Space size={4}>
                  <Text strong>{name ?? '—'}</Text>
                  {row.syncStatus === 'SYNCED' ? (
                    <Tooltip title="已在空中云汇创建 beneficiary">
                      <Tag color="green">已同步</Tag>
                    </Tooltip>
                  ) : (
                    <Tooltip title="只在本地，第一次打款前才推送给空中云汇">
                      <Tag>草稿</Tag>
                    </Tooltip>
                  )}
                </Space>
                {row.nickname && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.nickname}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            title: '类型',
            dataIndex: 'entityType',
            width: 90,
            render: (v: string | null) => (v === 'COMPANY' ? <Tag>企业</Tag> : <Tag>个人</Tag>),
          },
          {
            title: '收款地区 / 币种',
            width: 150,
            render: (_, row) => `${row.bankCountryCode ?? '—'} / ${row.accountCurrency ?? '—'}`,
          },
          {
            title: '汇款方式',
            width: 140,
            render: (_, row) => (
              <Space size={4}>
                <Tag color="blue">{row.transferMethod ?? '—'}</Tag>
                {row.localClearingSystem && <Tag>{row.localClearingSystem}</Tag>}
              </Space>
            ),
          },
          {
            // 围绕这个收款人的钱现在在哪。桶的口径见后端 attachPayoutSummary，
            // 🔴「状态未知」单独一行而不是并进「进行中」：那是「需人工去后台核对」的意思，
            // 和正常在途的钱混在一起，看的人就再也发现不了它（PRODUCT.md §5.7）。
            title: '打款情况',
            width: 200,
            render: (_, row) =>
              row.payoutSummary.length === 0 ? (
                <Text type="secondary">—</Text>
              ) : (
                <Space direction="vertical" size={2}>
                  {row.payoutSummary.map((b) => (
                    <Tooltip
                      key={`${b.key}-${b.currency}`}
                      title={`${PAYOUT_BUCKET_HINT[b.key]}（共 ${b.count} 笔）`}
                    >
                      <Space size={4}>
                        <Tag color={PAYOUT_BUCKET_COLOR[b.key]} style={{ marginInlineEnd: 0 }}>
                          {PAYOUT_BUCKET_LABEL[b.key]}
                        </Tag>
                        {/* 金额全程字符串，千分位也在字符串上加 —— 绝不进 Number */}
                        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {groupDigits(b.amount)} {b.currency}
                        </Text>
                      </Space>
                    </Tooltip>
                  ))}
                </Space>
              ),
          },
          {
            // 「谁弄进来的」合成一列：拆成创建人 / 是否外部 / 链接创建人 / 填写人四列的话，
            // 每一列都有一半是空的，看的人还要自己拼回一句话
            title: '来源',
            width: 260,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Space size={4}>
                  {row.origin.kind === 'external' ? (
                    <Tooltip title="对外收集链接填回来的">
                      <Tag color="orange">对外收集</Tag>
                    </Tooltip>
                  ) : (
                    <Tag>站内添加</Tag>
                  )}
                  <Text style={{ fontSize: 12 }}>{originSummary(row)}</Text>
                </Space>
                {row.contactEmail && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    联系邮箱：{row.contactEmail}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            title: '创建时间',
            dataIndex: 'createdAt',
            width: 160,
            render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
          },
          // 🔴 没有删除：收款人一旦创建就永久保留（PRODUCT.md §4.3）
        ]}
      />

      <PayeeDetailDrawer payee={detail} open={detail !== null} onClose={() => setDetail(null)} />

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="新建收款人"
        size={560}
        destroyOnHidden
        // 放在标题栏而不是表单里：它不属于任何一个字段，是「换一种方式填整张表」
        extra={
          <Button icon={<ThunderboltOutlined />} onClick={() => setAiOpen(true)} disabled={submitting}>
            AI 快速录入
          </Button>
        }
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setOpen(false)}>取消</Button>
            <Button type="primary" loading={submitting} disabled={!schema} onClick={submit}>
              校验并创建
            </Button>
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          表单字段由 Airwallex 按「国家 × 币种 × 汇款方式 × 主体类型」实时下发，
          改动上面的选项会自动重新加载下面的字段。
        </Paragraph>

        {schemaLoading && !schema && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Spin />
          </div>
        )}

        {schema && (
          <Spin spinning={schemaLoading}>
            <SchemaForm
              schema={schema}
              values={values}
              errors={errors}
              disabled={submitting}
              onChange={onFieldChange}
            />
          </Spin>
        )}

        {Object.keys(errors).length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 8 }}
            title="有字段不符合要求"
            description="标红的字段需要修正后再提交。"
          />
        )}

        <AiQuickFillModal open={aiOpen} onClose={() => setAiOpen(false)} onApply={(r) => void applyAi(r)} />
      </Drawer>
    </Card>
  );
}
